import * as THREE from 'three';
import { binGeometryFromBin } from 'bin-3d-feed-volume';
import CameraControls from 'camera-controls';
CameraControls.install({ THREE: THREE });

import { getMaterials } from './materials';
import { generateVolume, generateSurface, computeFeedXYTransformValues } from './feed-generator';
import { generateLipAndLid, generateLegs, generateBinShell, generateRings } from './circular-bin-generator';
import * as RectangularBinGenerator from './rectangular-bin-generator';

const SEGMENTS_IN_CYLINDER = 60;
const GROUND_PLANE_VERTICLE_OFFSET = -0.025;
const LIP_LID_VERTICLE_OFFSET = -0.01;
const MAX_ADDITIONAL_DISTANCE = 15;
const MIN_SUBTRACTED_DISTANCE = -30;
const HIDE_LIP_LID_AT_POLAR_ANGLE = 0.4;
const SHRINK_FEED_XY_SCALE = -0.0015;
const MAX_ADDITIONAL_CAMERA_Y = 0.35;
const AVERAGE_BIN_HEIGHT = 7.9;

export class BinRenderer {
  constructor(props) {
    this._props = props;
    this._bin = props.bin;
    this._binGeometry = props.binGeometry || binGeometryFromBin(this._bin);
    this._deviceId = props.deviceId;
    this._heightMap = props.heightMap;
    this._resolution = props.resolution;
    this._feedMask = props.feedMask;
    this._extrapolation = props.extrapolation || 'rbf-polar-bias';
    this._autoRotate = props.autoRotate || false;
    this._autoRotateSpeed = props.autoRotateSpeed || 1;
    this._userControl = props.userControl === undefined ? true : props.userControl;

    this._hideLegs = props.hideLegs || false;
    this._hideRings = props.hideRings || false;
    this._hideBin = props.hideBin || false;
    this._solidBinWalls = props._solidBinWalls || false;
    this._colors = props.colors || {};

    // Begin with both as dirty as the initial generation has not happened
    this._dirtyFeed = true;
    this._dirtyBin = true;
    this._dirtyRender = true;

    this._resizeObserver = undefined;
    this._domMount = undefined;

    this._clipPosition = props.clipPosition || 2;

    /**
     * All THREE.js objects
     */
    this._3d = {
      scene: undefined,
      renderer: undefined,
      camera: undefined,
      orbit: undefined,
      groupMain: undefined,
      materials: getMaterials(this._colors),
      feed: {
        groupOuter: undefined,
        groupInner: undefined,
        surface: undefined,
        alongHopper: undefined,
        volume: undefined,
      },
      bin: {
        groupShell: undefined,
        groupLegs: undefined,
        groupLipAndLid: undefined,
        groupRings: undefined,
      },
    };

    this.animating = true;
  }

  /**
   * Whether the bin or feed is out of sync with what is currently generated
   * If true, the next call to generate will cause some new meshes to be generated
   */
  get dirty() {
    return this._dirtyFeed || this._dirtyBin;
  }

  get mounted() {
    return !!this._domMount;
  }

  get binGeometry() {
    return this._binGeometry;
  }

  set clipPosition(newClipPosition) {
    this._clipPosition = newClipPosition;
    this._dirtyRender = true;
  }

  get clipPosition() {
    return this._clipPosition;
  }

  set bin(newBin) {
    this._bin = newBin;
    this._dirtyBin = true;
    // If bin dimensions have been changed, then feed must be re-rendered as well
    this._dirtyFeed = true;
  }

  set deviceId(deviceId) {
    this._deviceId = deviceId;
    this._dirtyFeed = true;
  }

  set resolution(newResolution) {
    this._resolution = newResolution;
    this._dirtyFeed = true;
  }

  set useFeedMask(shouldUseFeedMask) {
    this._useFeedMask = shouldUseFeedMask;
    this._dirtyFeed = true;
  }

  set feedMask(newFeedMask) {
    this._feedMask = newFeedMask;
    this._dirtyFeed = true;
  }

  set extrapolation(newExtrapolation) {
    this._extrapolation = newExtrapolation;
    this._dirtyFeed = true;
  }

  set autoRotate(newAutoRotate) {
    if (!this.mounted) {
      throw new Error('Cannot set properties before a call to mount()');
    }
    this._autoRotate = newAutoRotate;
  }

  get autoRotate() {
    return this._autoRotate;
  }

  set userControl(newUserControl) {
    this._3d.orbit.enabled = newUserControl;
    this._userControl = newUserControl;
  }

  get userControl() {
    return this._userControl;
  }

  set hideLegs(legsHidden) {
    this._hideLegs = legsHidden;
    this._3d.bin.groupLegs.visible = !this._hideLegs;
    this._dirtyRender = true;
  }

  get hideLegs() {
    return this._hideLegs;
  }

  set hideRings(ringsHidden) {
    this._hideRings = ringsHidden;
    this._3d.bin.groupRings.visible = !this._hideRings;
    this._dirtyRender = true;
  }

  get hideRings() {
    return this._hideRings;
  }

  set hideBin(binHidden) {
    this._hideBin = binHidden;
    this._3d.bin.groupShell.visible = !this._hideBin;
    this._dirtyRender = true;
  }

  get hideBin() {
    return this._hideBin;
  }

  set solidBinWalls(solidWalls) {
    this._solidBinWalls = solidWalls;
    this._3d.materials.binMaterial.side = this._solidBinWalls ? THREE.DoubleSide : THREE.BackSide;
    this._3d.materials.lidLipMaterial.side = this._3d.materials.binMaterial.side;
    this._dirtyRender = true;
  }

  get solidBinWalls() {
    return this._solidBinWalls;
  }

  set colors(colors) {
    this._colors = colors;
    this._3d.materials = getMaterials(this._colors);
    this.generate(true);
  }

  set heightMap(newHeightMap) {
    this._heightMap = newHeightMap;
    this._dirtyFeed = true;
  }

  generate(force = false) {
    if (this._dirtyBin || force) {
      this._generateBinAndFloor();
      this._dirtyRender = true;
    }

    if (this._dirtyFeed || force) {
      this._renderFeed();
      this._dirtyRender = true;
    }
  }

  _renderFeed() {
    if (this._3d.feed.surface) {
      this._3d.feed.groupInner.remove(this._3d.scene.getObjectByName(this._3d.feed.surface.name));
      this._3d.feed.groupInner.remove(this._3d.scene.getObjectByName(this._3d.feed.alongHopper.name));
      this._3d.feed.groupInner.remove(this._3d.scene.getObjectByName(this._3d.feed.volume.name));
    }

    const heightMap = this._heightMap;

    const { meshes: surfaceMeshes } = generateSurface(heightMap, this._bin, this._binGeometry, this._resolution, this._3d.materials);
    this._3d.feed.surface = surfaceMeshes;
    this._3d.feed.surface.name = `${Math.random()}`;
    this._3d.feed.groupInner.add(this._3d.feed.surface);

    this._3d.feed.alongHopper = generateSurface(heightMap, this._bin, this._binGeometry, this._resolution, this._3d.materials, false).meshes;
    this._3d.feed.alongHopper.name = `${Math.random()}`;
    this._3d.feed.groupInner.add(this._3d.feed.alongHopper);

    this._3d.feed.volume = generateVolume(heightMap, this._bin, this._binGeometry, this._resolution, this._3d.materials);
    this._3d.feed.volume.name = `${Math.random()}`;
    this._3d.feed.groupInner.add(this._3d.feed.volume);

    const { offsetX, offsetY, scaleX, scaleY } = computeFeedXYTransformValues(this._bin, this._binGeometry, this._resolution);
    this._3d.feed.groupInner.position.set(offsetX, offsetY, this._bin.dimensions.totalHeight / 2);
    this._3d.feed.groupOuter.scale.set(scaleX + SHRINK_FEED_XY_SCALE, scaleY + SHRINK_FEED_XY_SCALE, 1);

    this._dirtyFeed = false;
  }

  _generateBinAndFloor() {
    if (this._3d.bin.groupShell) {
      this._3d.groupMain.remove(this._3d.groupMain.getObjectByName(this._3d.bin.groupShell.name));
      this._3d.scene.remove(this._3d.scene.getObjectByName(this._3d.groundPlane.name));
    }
    const { dimensions } = this._bin;

    this.solidBinWalls = this._solidBinWalls;
    this._3d.bin.groupShell = new THREE.Group();
    this._3d.bin.groupShell.name = 'group-shell';
    this._3d.bin.groupShell.visible = !this._hideBin;
    this._3d.bin.groupShell.position.z = dimensions.totalHeight / 2;
    this._3d.groupMain.add(this._3d.bin.groupShell);

    const {
      binMeshes,
      legGroup,
      lipAndLidGroup,
      ringsGroup,
      offsets,
      groundZ,
    } = this._bin.dimensions.shape === 'circular' ? this._generateCircularBin() : this._generateRectangularBin();

    this._3d.bin.groupShell.position.x = offsets.x;
    this._3d.bin.groupShell.position.y = offsets.y;

    // Add bin shell
    for (const mesh of binMeshes) {
      this._3d.bin.groupShell.add(mesh);
    }

    // Add legs
    this._3d.bin.groupLegs = legGroup;
    this._3d.bin.groupLegs.visible = !this._hideLegs;
    this._3d.bin.groupShell.add(this._3d.bin.groupLegs);
    // Add lip and lid
    this._3d.bin.groupLipAndLid = lipAndLidGroup;
    this._3d.bin.groupShell.add(this._3d.bin.groupLipAndLid);
    // Add rings
    this._3d.bin.groupRings = ringsGroup;
    this._3d.bin.groupRings.visible = !this._hideRings;
    this._3d.bin.groupShell.add(this._3d.bin.groupRings);

    this._3d.groundPlane = new THREE.Mesh(
      new THREE.PlaneGeometry(1000, 1000),
      this._3d.materials.groundMaterial
    );
    this._3d.groundPlane.name = 'groundPlane';
    this._3d.groundPlane.position.y = this._3d.bin.groupShell.position.z + groundZ + GROUND_PLANE_VERTICLE_OFFSET;
    this._3d.groundPlane.rotation.x = -Math.PI / 2;
    this._3d.groundPlane.receiveShadow = true;
    this._3d.scene.add(this._3d.groundPlane);

    this._dirtyBin = false;
  }

  _generateCircularBin() {
    const { dimensions } = this._bin;
    const binMeshes = generateBinShell(dimensions, SEGMENTS_IN_CYLINDER, this._3d.materials);
    const legsData = generateLegs(this._bin, 4, 0.25, 0.125, this._3d.materials);
    const { bottomOfLegs } = legsData;

    const lipAndLid = generateLipAndLid(this._bin, this._3d.materials);
    lipAndLid.position.z = LIP_LID_VERTICLE_OFFSET;

    const rings = generateRings(dimensions, SEGMENTS_IN_CYLINDER, this._3d.materials);

    return {
      binMeshes,
      legGroup: legsData.legGroup,
      lipAndLidGroup: lipAndLid,
      ringsGroup: rings,
      groundZ: bottomOfLegs,
      offsets: {
        x: 0,
        y: 0,
      },
    };
  }

  _generateRectangularBin() {
    const { dimensions } = this._bin;
    const binMeshes = RectangularBinGenerator.generateBinShell(dimensions, this._3d.materials);

    return {
      binMeshes,
      legGroup: new THREE.Group(),
      lipAndLidGroup: new THREE.Group(),
      ringsGroup: new THREE.Group(),
      groundZ: -dimensions.totalHeight,
      offsets: {
        x: -dimensions.body.width / 2,
        y: -dimensions.body.depth / 2,
      },
    };
  }

  _onDomMountResized() {
    const width = this._domMount.clientWidth;
    const height = this._domMount.clientHeight;
    this._3d.renderer.setSize(width, height);
    this._3d.camera.aspect = width / height;
    this._3d.camera.updateProjectionMatrix();
    this._dirtyRender = true;
  }

  unmount() {
    this._domMount.removeChild(this._3d.renderer.domElement);
    this._resizeObserver && this._resizeObserver.disconnect();

    this._3d.scene.traverse((obj) => {
      if (obj.geometry) {
        obj.geometry.dispose();
      }

      if (obj.material) {
        if (obj.material.length) {
          for (let i = 0; i < obj.material.length; ++i) {
            obj.material[i].dispose();
          }
        } else {
          obj.material.dispose();
        }
      }
    });

    this._3d.renderer.renderLists.dispose();
  }

  mount(domMount) {
    const { dimensions } = this._bin;
    const maxXZDimension = dimensions.shape === 'circular' ? (dimensions.body.radius * 2) : Math.max(dimensions.body.width, dimensions.body.depth);
    const maxXYZDimension = Math.max(maxXZDimension, dimensions.totalHeight);
    const aspectRatio = maxXZDimension / dimensions.totalHeight;

    this._domMount = domMount;
    this._resizeObserver = new ResizeObserver(this._onDomMountResized.bind(this)).observe(this._domMount);
    const width = this._domMount.clientWidth;
    const height = this._domMount.clientHeight;
    const whiteColor = new THREE.Color(0xffffff);

    this._3d.scene = new THREE.Scene();
    this._3d.scene.background = whiteColor;
    this._3d.camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);

    const additionalY = Math.min(Math.max(dimensions.totalHeight - AVERAGE_BIN_HEIGHT, 0) / 15, 1) * MAX_ADDITIONAL_CAMERA_Y;
    // These are the normalized values of a nice angle to view most bins
    this._3d.camera.position.z = 0.9486832980505138;
    this._3d.camera.position.y = 0.3162277660168379 + additionalY;

    // Zoom out with large bins to be able to view the whole bin
    this._3d.camera.position.multiplyScalar(Math.max(AVERAGE_BIN_HEIGHT, maxXYZDimension));
    this._3d.renderer = new THREE.WebGLRenderer({ antialias: true });
    this._3d.renderer.localClippingEnabled = true;
    this._3d.renderer.setClearColor(whiteColor, 0);
    this._3d.renderer.setSize(width, height);
    this._3d.renderer.render(this._3d.scene, this._3d.camera);
    this._3d.renderer.shadowMap.enabled = true;
    this._3d.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    this._domMount.appendChild(this._3d.renderer.domElement);

    const light = new THREE.HemisphereLight(0xffffff, 0xffffff, 0.94);
    light.position.set(0.5, 1, 0.25);
    light.castShadow = false;
    this._3d.scene.add(light);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.3);
    directionalLight.shadow.mapSize.width *= 2;
    directionalLight.shadow.mapSize.height *= 2;
    directionalLight.castShadow = true;
    directionalLight.shadow.camera.top = -25;
    directionalLight.shadow.camera.right = 25;
    directionalLight.shadow.camera.bottom = 25;
    directionalLight.shadow.camera.left = -25;
    directionalLight.shadow.bias = -0.00025;
    directionalLight.position.set(0.3333333333333333, 0.6666666666666666, 0.6666666666666666);
    directionalLight.position.multiplyScalar(300);
    directionalLight.target.position.set(0, 0, 0);
    this._3d.scene.add(directionalLight);
    this._3d.scene.add(directionalLight.target);

    this._3d.groupMain = new THREE.Group();
    this._3d.groupMain.rotateX(-Math.PI / 2);

    this._generateBinAndFloor();

    // When the aspect ratio is very low (ie the bin is tall & thin), we need to move the target of the camera upwards
    // to keep the entire bin in view.
    const maxTargetY = dimensions.totalHeight / 8;
    const aspectRatioScoreRaw = (0.25 / Math.min(aspectRatio, 0.25)) - 1;
    const aspectRatioScore = Math.min(aspectRatioScoreRaw, 2);

    this._3d.cameraControls = new CameraControls(this._3d.camera, this._3d.renderer.domElement);
    this._3d.cameraControls.enabled = this._userControl;
    this._3d.cameraControls.truckSpeed = 0;
    this._3d.cameraControls.maxPolarAngle = Math.PI / 2;
    this._3d.cameraControls.zoomSpeed = 0.5;
    this._3d.cameraControls.rotateSpeed = 1.0;
    this._3d.cameraControls.setTarget(0, aspectRatioScore * maxTargetY, 0, false);
    this._3d.cameraControls.minDistance = Math.max(this._3d.cameraControls.distance + MIN_SUBTRACTED_DISTANCE, 0.5);
    this._3d.cameraControls.maxDistance = this._3d.cameraControls.distance + MAX_ADDITIONAL_DISTANCE;

    this._3d.feed.groupOuter = new THREE.Group();
    // This scaling is tuned to JUST put the feed entirely inside the bin model
    this._3d.feed.groupOuter.scale.set(0.975, 0.975, 0.975);
    this._3d.feed.groupOuter.position.set(0.0, 0.0, (dimensions.totalHeight / 4) * (1 - 0.975));
    this._3d.feed.groupInner = new THREE.Group();
    this._3d.feed.groupOuter.add(this._3d.feed.groupInner);
    this._3d.groupMain.add(this._3d.feed.groupOuter);
    this._3d.scene.add(this._3d.groupMain);

    this._renderFeed();

    // Don't show anything until the first frame has rendered
    // This avoids black flashes on startup
    let firstFrameAnimated = false;
    this._3d.renderer.domElement.style.visibility = 'hidden';

    // Manage camera-controller auto rotate transitioning to and from user control
    let userDragging = false;
    let disableAutoRotate = false;
    const onRest = () => {
      this._3d.cameraControls.removeEventListener('rest', onRest);
      userDragging = false;
      disableAutoRotate = false;
    };
    this._3d.cameraControls.addEventListener('controlstart', () => {
      this._3d.cameraControls.removeEventListener('rest', onRest);
      userDragging = true;
      disableAutoRotate = true;
    });
    this._3d.cameraControls.addEventListener('controlend', () => {
      if (this._3d.cameraControls.active) {
        this._3d.cameraControls.addEventListener('rest', onRest);
      } else {
        onRest();
      }
    });
    this._3d.cameraControls.addEventListener('transitionstart', () => {
      if (userDragging) {
        return;
      }

      disableAutoRotate = true;
      this._3d.cameraControls.addEventListener('rest', onRest);
    });

    const clock = new THREE.Clock();
    var animate = () => {
      const delta = clock.getDelta();
      const hasControlsUpdated = this._3d.cameraControls.update(delta);
      if (this._autoRotate && !disableAutoRotate) {
        this._3d.cameraControls.azimuthAngle += (this._autoRotateSpeed * -5) * delta * THREE.MathUtils.DEG2RAD;
      }
      this._3d.camera.getWorldPosition(this._3d.materials.clippingPlane.normal);

      this._3d.materials.clippingPlane.normal.y = 0;
      this._3d.materials.clippingPlane.normalize().negate();
      this._3d.materials.clippingPlane.constant = this._clipPosition * maxXZDimension;

      // Don't bother with this math if you cant actually move the camera
      if (this._3d.cameraControls.enabled === true) {
        if (this._3d.cameraControls.polarAngle < HIDE_LIP_LID_AT_POLAR_ANGLE) {
          this.setGroupInvisibleWithShadows(this._3d.bin.groupLipAndLid, false);
        } else {
          this.setGroupInvisibleWithShadows(this._3d.bin.groupLipAndLid, true);
        }
      }

      if (this.animating) {
        requestAnimationFrame(animate);
      }

      if (!firstFrameAnimated || hasControlsUpdated || this._dirtyRender) {
        this._3d.renderer.render(this._3d.scene, this._3d.camera);
        this._dirtyRender = false;
      }

      if (!firstFrameAnimated) {
        firstFrameAnimated = true;
        this._3d.renderer.domElement.style.visibility = 'visible';
      }
    };
    animate();
  }

  setGroupInvisibleWithShadows(group, visible) {
    for (const child of group.children) {
      if (child.type === 'Group') {
        this.setGroupInvisibleWithShadows(child, visible);
        continue;
      }
      child.material.colorWrite = visible;
      child.material.depthWrite = visible;
    }
  }
}
